import { Button, Empty, Typography } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import CondensedTable from 'components/CondensedTable/CondensedTable';
import Pill from 'components/Pill/Pill';

import { formatDate, formatDateTime } from 'utilities/datesAndTimes';
import { formatPhoneNumber } from 'utilities/helpers';
import { getColumnSearchProps } from 'utilities/tableHelpers';

import { REASON_CODES } from './constants';

const getBlockStatus = (record) => {
  const { blockEnd, isBlocked } = record;

  if (!isBlocked) {
    return { label: 'Expired', color: null };
  }

  if (!blockEnd) {
    return { label: 'Permanent Block', color: 'red' };
  }

  return { label: 'Active', color: 'orange' };
};

const BlockedPhoneHistoryTable = ({
  data,
  loading,
  onEdit,
  hidePagination = false,
  hideActions = false,
}) => {
  const searchFilter = (dataIndex, value, record) => {
    const indexValue = record[dataIndex];
    return indexValue?.toString().toLowerCase().includes(value.toLowerCase());
  };

  const phoneSearchFilter = (dataIndex, value, record) => {
    const phoneNumber = record[dataIndex];
    const searchDigits = value.replace(/\D/g, '');
    return phoneNumber?.includes(searchDigits);
  };

  const columns = [
    {
      title: '',
      key: 'actions',
      width: 48,
      fixed: 'left',
      render: (_, record) => {
        if (hideActions) return null;
        return (
          <Button
            type="text"
            icon={<EditOutlined />}
            size="small"
            onClick={() => onEdit(record)}
            data-testid="edit-blocked-phone-button"
          />
        );
      },
    },
    {
      title: 'Phone Number',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      width: 150,
      ...getColumnSearchProps('phoneNumber', phoneSearchFilter),
      sorter: (a, b) => a.phoneNumber.localeCompare(b.phoneNumber),
      render: (phoneNumber) => formatPhoneNumber(phoneNumber),
    },
    {
      title: 'Customer ID',
      dataIndex: 'customerIds',
      key: 'customerId',
      width: 120,
      render: (customerIds) => customerIds?.[0] || '-',
    },
    {
      title: 'Status',
      key: 'status',
      filters: [
        { text: 'Active', value: 'active' },
        { text: 'Permanent Block', value: 'permanent' },
        { text: 'Expired', value: 'expired' },
      ],
      onFilter: (value, record) => {
        const status = getBlockStatus(record);
        if (value === 'active') return status.label === 'Active';
        if (value === 'permanent') return status.label === 'Permanent Block';
        if (value === 'expired') return status.label === 'Expired';
        return false;
      },
      render: (_, record) => {
        const status = getBlockStatus(record);
        return <Pill color={status.color}>{status.label}</Pill>;
      },
    },
    {
      title: 'Block Start',
      dataIndex: 'blockStart',
      key: 'blockStart',
      render: (date) => formatDate(date, true),
      sorter: (a, b) => dayjs(a.blockStart).unix() - dayjs(b.blockStart).unix(),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Block End',
      dataIndex: 'blockEnd',
      key: 'blockEnd',
      render: (date) => (date ? formatDate(date, true) : 'Permanent'),
      sorter: (a, b) => {
        if (!a.blockEnd && !b.blockEnd) return 0;
        if (!a.blockEnd) return 1;
        if (!b.blockEnd) return -1;
        return dayjs(a.blockEnd).unix() - dayjs(b.blockEnd).unix();
      },
    },
    {
      title: 'Reason Code',
      dataIndex: 'reasonCode',
      key: 'reasonCode',
      filters: REASON_CODES.map((reasonCode) => ({
        text: reasonCode.label,
        value: reasonCode.value,
      })),
      onFilter: (value, record) => record.reasonCode === value,
      render: (code) => {
        const option = REASON_CODES.find(
          (reasonCode) => reasonCode.value === code,
        );
        return option ? option.label : code;
      },
    },
    {
      title: 'Created By',
      dataIndex: 'insertBy',
      key: 'insertBy',
      ...getColumnSearchProps('insertBy', searchFilter),
    },
    {
      title: 'Reason Description',
      dataIndex: 'reasonDescription',
      key: 'reasonDescription',
      width: 250,
      render: (text) => {
        if (!text) return '-';
        return (
          <div style={{ position: 'relative' }}>
            <Typography.Paragraph
              ellipsis={{
                rows: 3,
                expandable: 'collapsible',
                symbol: (expanded) => (expanded ? 'Read less' : 'Read more'),
              }}
              style={{ marginBottom: 0, whiteSpace: 'pre-line' }}
            >
              {text}
            </Typography.Paragraph>
          </div>
        );
      },
    },
    {
      title: 'Last Modified',
      key: 'lastModified',
      render: (_, record) => {
        if (record.lastModifiedBy || record.lastModifiedDate) {
          return (
            <div>
              {record.lastModifiedBy && <div>{record.lastModifiedBy}</div>}
              {record.lastModifiedDate && (
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: 'var(--font-size-sm)' }}
                >
                  {formatDateTime(record.lastModifiedDate, true)}
                </Typography.Text>
              )}
            </div>
          );
        }
        return '-';
      },
    },
  ];

  return (
    <CondensedTable
      columns={columns}
      data={data || []}
      length={data?.length || 0}
      loading={loading}
      rowKey="id"
      pagination={
        hidePagination
          ? false
          : {
              pageSize: 8,
              showTotal: (total, range) =>
                `Showing ${range[0]}-${range[1]} of ${total} records`,
            }
      }
      scroll={{ x: 1468 }}
      locale={{
        emptyText: (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No blocked phone numbers"
          />
        ),
      }}
    />
  );
};

export default BlockedPhoneHistoryTable;
